import { useState, useEffect } from "react";
import axios from "axios";
import {
  Factory, Plus, Pencil, Trash2, Upload, Download, Image as ImageIcon,
  MapPin, Truck, AlertTriangle, Eye, Search, GitFork, Copy as CopyIcon,
  FileText, X, ArrowRightLeft, RefreshCw,
} from "lucide-react";
import { saveAs } from "file-saver";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const emptyForm = {
  mould_code: "",
  part_name: "",
  polymer_components: [{ polymer_rm_id: "", percentage: 100 }],
  per_unit_weight_g: "",
  mb_ratio_pct: "",
  wastage_polymer: 1.02,
  wastage_mb: 1.00,
  cavities: 1,
  // Ops & logistics (Feb 2026) — surfaced on card + bulk-template
  cycle_time_sec: "",
  compatible_machines: "",   // comma-separated; split on submit
  mould_length_mm: "",
  mould_breadth_mm: "",
  mould_height_mm: "",
  mould_weight_kg: "",
  location_type: "BRANCH",
  branch_id: "",
  vendor_id: "",
  location_note: "",
};

export default function Moulds() {
  const [moulds, setMoulds] = useState([]);
  const [branches, setBranches] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [polymers, setPolymers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [locationFilter, setLocationFilter] = useState("ALL");

  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [imageFile, setImageFile] = useState(null);

  const [showAudit, setShowAudit] = useState(false);
  const [auditData, setAuditData] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);

  const [showUpload, setShowUpload] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadDryRun, setUploadDryRun] = useState(true);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadBusy, setUploadBusy] = useState(false);

  // Feb 2026 — Sub-mould bulk upload + family parent cleanup
  const [showSubBulk, setShowSubBulk] = useState(false);
  const [subBulkFile, setSubBulkFile] = useState(null);
  const [subBulkDryRun, setSubBulkDryRun] = useState(true);
  const [subBulkResult, setSubBulkResult] = useState(null);
  const [subBulkBusy, setSubBulkBusy] = useState(false);
  const [showCleanupResult, setShowCleanupResult] = useState(null); // {cleaned, skipped, no_change}
  const [cleanupBusy, setCleanupBusy] = useState(false);
  // Feb 2026 — Adopt orphan slash-coded sub-moulds
  const [adoptBusy, setAdoptBusy] = useState(false);
  const [adoptPlan, setAdoptPlan] = useState(null);      // dry-run result
  const [adoptApplied, setAdoptApplied] = useState(null); // apply result

  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [imageBlobUrls, setImageBlobUrls] = useState({});  // mould_id -> blob URL

  // Feb 2026 — Family/Copies/Docs (Mould Library Phase B)
  const [showSubMould, setShowSubMould] = useState(null);   // {parent}
  const [subMouldForm, setSubMouldForm] = useState({
    mould_code: "", part_name: "", per_unit_weight_g: "", mb_ratio_pct: "",
    cavities: 1, wastage_polymer: 1.02, wastage_mb: 1.00,
  });
  const [showCopies, setShowCopies] = useState(null);       // {mould}
  const [copyForm, setCopyForm] = useState({ location_type: "BRANCH", branch_id: "", vendor_id: "", note: "" });
  const [showDocs, setShowDocs] = useState(null);           // {mould}
  const [docs, setDocs] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [docFile, setDocFile] = useState(null);
  const [docType, setDocType] = useState("OTHER");
  const [docUploading, setDocUploading] = useState(false);
  const [dup409, setDup409] = useState(null);               // {payload, existing}
  const [copyMoveState, setCopyMoveState] = useState(null); // {copy_id, location_type, branch_id, vendor_id, note}

  const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

  // ---------------- Fetchers ----------------
  const loadAll = async () => {
    setLoading(true);
    try {
      const [mRes, brRes, vRes, polyRes] = await Promise.all([
        axios.get(`${API}/moulds?status=ACTIVE`, auth()),
        axios.get(`${API}/branches`, auth()).catch(() => ({ data: [] })),
        axios.get(`${API}/vendors`, auth()).catch(() => ({ data: [] })),
        axios.get(`${API}/raw-materials?category=INP_POLY&limit=2000`, auth()).catch(() => ({ data: { items: [] } })),
      ]);
      setMoulds(mRes.data || []);
      // Feb 2026 — dedupe branches/vendors by id to squash React "duplicate
      // key" warnings from any upstream API returning a repeated row.
      const dedupeById = (arr) => Array.from(new Map((arr || []).map(x => [x.id, x])).values());
      setBranches(dedupeById(brRes.data));
      const vList = Array.isArray(vRes.data) ? vRes.data : (vRes.data?.items || []);
      setVendors(dedupeById(vList));
      // POLYmer raw materials — try to fetch all RMs starting with POLY_
      const polyItems = polyRes.data?.items || polyRes.data || [];
      setPolymers(polyItems.filter(rm => (rm.rm_id || "").startsWith("POLY")));
    } catch (e) {
      toast.error(`Failed to load: ${e.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Fallback: load polymer RMs via the generic raw-materials endpoint
  const loadPolymersFallback = async () => {
    try {
      const r = await axios.get(`${API}/raw-materials?search=POLY_&limit=500`, auth());
      const items = r.data?.items || r.data || [];
      const polys = items.filter(rm => (rm.rm_id || "").startsWith("POLY"));
      if (polys.length) setPolymers(polys);
    } catch {/* ignore */}
  };

  useEffect(() => {
    loadAll();
  }, []);

  useEffect(() => {
    if (polymers.length === 0) loadPolymersFallback();
  }, [moulds]);

  // Lazy-load image thumbnails as blob URLs (auth header needs fetch, not <img src>)
  useEffect(() => {
    moulds.forEach(async (m) => {
      if (m.image_storage_path && !imageBlobUrls[m.id]) {
        try {
          const r = await axios.get(`${API}/moulds/${m.id}/image`, { ...auth(), responseType: "blob" });
          const url = URL.createObjectURL(r.data);
          setImageBlobUrls(prev => ({ ...prev, [m.id]: url }));
        } catch {/* ignore */}
      }
    });
    return () => Object.values(imageBlobUrls).forEach(URL.revokeObjectURL);
  }, [moulds]);

  // ---------------- Filters ----------------
  // Feb 2026 — Family Sub-Moulds: sub-moulds render as chips inside
  // their parent card, NOT as separate top-level tiles. Filter them
  // out of the primary grid; group them for the parent-card overlay.
  const subMouldsByParent = moulds.reduce((acc, m) => {
    if (m.parent_mould_id) {
      (acc[m.parent_mould_id] = acc[m.parent_mould_id] || []).push(m);
    }
    return acc;
  }, {});
  const topLevel = moulds.filter(m => !m.parent_mould_id);
  const filtered = topLevel.filter(m => {
    const s = search.trim().toLowerCase();
    if (locationFilter !== "ALL" && m.location_type !== locationFilter) return false;
    if (!s) return true;
    const children = subMouldsByParent[m.id] || [];
    return (
      (m.mould_code || "").toLowerCase().includes(s) ||
      (m.part_name || "").toLowerCase().includes(s) ||
      (m.model_name || "").toLowerCase().includes(s) ||
      (m.branch_name || "").toLowerCase().includes(s) ||
      (m.vendor_name || "").toLowerCase().includes(s) ||
      children.some(c => (c.mould_code || "").toLowerCase().includes(s) || (c.part_name || "").toLowerCase().includes(s))
    );
  });

  // ---------------- Save ----------------
  const openCreate = () => { setEditing(null); setForm(emptyForm); setImageFile(null); setShowDialog(true); };
  const openEdit = (m) => {
    setEditing(m);
    // Feb 2026 — Sub-moulds inherit polymer_components + location from
    // parent. For a clean edit UX, look the parent up in the loaded
    // list and prefill inherited fields (readonly-ish in the payload
    // path, since saveMould strips them for sub-moulds).
    const parent = m.is_sub_mould && m.parent_mould_id
      ? moulds.find(x => x.id === m.parent_mould_id)
      : null;
    // Migrate single-polymer moulds into the new blend shape on edit.
    // For sub-moulds, always pull the parent's blend so the user can see
    // what's inherited (the field is disabled below on the form).
    let components;
    if (parent) {
      components = (parent.polymer_components && parent.polymer_components.length > 0)
        ? parent.polymer_components.map(c => ({ polymer_rm_id: c.polymer_rm_id, percentage: c.percentage }))
        : (parent.polymer_rm_id ? [{ polymer_rm_id: parent.polymer_rm_id, percentage: 100 }] : [{ polymer_rm_id: "", percentage: 100 }]);
    } else {
      components = (m.polymer_components && m.polymer_components.length > 0)
        ? m.polymer_components.map(c => ({ polymer_rm_id: c.polymer_rm_id, percentage: c.percentage }))
        : (m.polymer_rm_id ? [{ polymer_rm_id: m.polymer_rm_id, percentage: 100 }] : [{ polymer_rm_id: "", percentage: 100 }]);
    }
    setForm({
      mould_code: m.mould_code,
      part_name: m.part_name || "",
      polymer_components: components,
      per_unit_weight_g: m.per_unit_weight_g ?? "",
      mb_ratio_pct: m.mb_ratio_pct ?? "",
      wastage_polymer: m.wastage_polymer ?? 1.02,
      wastage_mb: m.wastage_mb ?? 1.00,
      cavities: m.cavities ?? 1,
      cycle_time_sec: m.cycle_time_sec ?? "",
      compatible_machines: Array.isArray(m.compatible_machines) ? m.compatible_machines.join(", ") : (m.compatible_machines || ""),
      mould_length_mm: m.mould_length_mm ?? "",
      mould_breadth_mm: m.mould_breadth_mm ?? "",
      mould_height_mm: m.mould_height_mm ?? "",
      mould_weight_kg: m.mould_weight_kg ?? "",
      location_type: (parent ? parent.location_type : m.location_type) || "BRANCH",
      branch_id: (parent ? parent.branch_id : m.branch_id) || "",
      vendor_id: (parent ? parent.vendor_id : m.vendor_id) || "",
      location_note: m.location_note || "",
    });
    setImageFile(null);
    setShowDialog(true);
  };

  // Polymer blend helpers
  const addBlendRow = () => {
    if (form.polymer_components.length >= 4) { toast.error("Max 4 polymer components"); return; }
    setForm({ ...form, polymer_components: [...form.polymer_components, { polymer_rm_id: "", percentage: 0 }] });
  };
  const removeBlendRow = (i) => {
    const next = form.polymer_components.filter((_, idx) => idx !== i);
    setForm({ ...form, polymer_components: next.length ? next : [{ polymer_rm_id: "", percentage: 100 }] });
  };
  const updateBlendRow = (i, field, value) => {
    const next = form.polymer_components.map((c, idx) => idx === i ? { ...c, [field]: value } : c);
    setForm({ ...form, polymer_components: next });
  };
  const blendPctSum = form.polymer_components.reduce((s, c) => s + (Number(c.percentage) || 0), 0);
  const blendIsValid = (
    form.polymer_components.length >= 1
    && form.polymer_components.length <= 4
    && form.polymer_components.every(c => c.polymer_rm_id)
    && Math.abs(blendPctSum - 100) <= 0.5
  );

  const saveMould = async () => {
    try {
      const blendComponents = form.polymer_components
        .filter(c => c.polymer_rm_id)
        .map(c => ({ polymer_rm_id: c.polymer_rm_id, percentage: Number(c.percentage) || 0 }));
      const payload = {
        mould_code: form.mould_code.trim().toUpperCase(),
        part_name: form.part_name || "",
        polymer_components: blendComponents,
        polymer_rm_id: blendComponents[0]?.polymer_rm_id || null,  // legacy back-compat
        per_unit_weight_g: Number(form.per_unit_weight_g) || 0,
        mb_ratio_pct: Number(form.mb_ratio_pct) || 0,
        wastage_polymer: Number(form.wastage_polymer) || 1.02,
        wastage_mb: Number(form.wastage_mb) || 1.00,
        cavities: Number(form.cavities) || 1,
        cycle_time_sec: Number(form.cycle_time_sec) || 0,
        compatible_machines: String(form.compatible_machines || "")
          .split(",").map(s => s.trim()).filter(Boolean),
        mould_length_mm: Number(form.mould_length_mm) || 0,
        mould_breadth_mm: Number(form.mould_breadth_mm) || 0,
        mould_height_mm: Number(form.mould_height_mm) || 0,
        mould_weight_kg: Number(form.mould_weight_kg) || 0,
        location_type: form.location_type,
        branch_id: form.location_type === "BRANCH" ? (form.branch_id || null) : null,
        vendor_id: form.location_type === "VENDOR" ? (form.vendor_id || null) : null,
        location_note: form.location_note || "",
      };
      let mouldId;
      let cascadeSummary = null;
      if (editing) {
        // Feb 2026 — For sub-moulds strip inherited fields (polymer +
        // location) from the PUT payload so the backend inheritance
        // guard (SUB_MOULD_INHERITS_FROM_PARENT) never fires on a
        // legit part-name/weight edit.
        const { mould_code: _mc, location_type: _lt, branch_id: _bi, vendor_id: _vi, location_note: _ln, ...physical } = payload;
        if (editing.is_sub_mould) {
          delete physical.polymer_components;
          delete physical.polymer_rm_id;
        }
        const r = await axios.put(`${API}/moulds/${editing.id}`, physical, auth());
        cascadeSummary = r.data?.cascade || null;
        // Location updates are also parent-inherited for sub-moulds.
        if (!editing.is_sub_mould && (
             form.location_type !== editing.location_type
             || form.branch_id !== editing.branch_id
             || form.vendor_id !== editing.vendor_id)) {
          await axios.put(`${API}/moulds/${editing.id}/location`, {
            location_type: form.location_type,
            branch_id: form.location_type === "BRANCH" ? form.branch_id : null,
            vendor_id: form.location_type === "VENDOR" ? form.vendor_id : null,
            note: form.location_note,
          }, auth());
        }
        mouldId = editing.id;
      } else {
        const r = await axios.post(`${API}/moulds`, payload, auth());
        mouldId = r.data.id;
      }
      if (imageFile) {
        const fd = new FormData();
        fd.append("file", imageFile);
        await axios.post(`${API}/moulds/${mouldId}/image`, fd, {
          ...auth(),
          headers: { ...auth().headers, "Content-Type": "multipart/form-data" },
        });
      }
      if (cascadeSummary && cascadeSummary.rewritten > 0) {
        toast.success(`Saved + cascaded BOMs for ${cascadeSummary.rewritten} INPs. Undo id: ${cascadeSummary.cascade_id.slice(0,8)}…`);
      } else {
        toast.success(editing ? "Mould updated" : "Mould created");
      }
      setShowDialog(false);
      loadAll();
    } catch (e) {
      // Feb 2026 — 409 duplicate-code path lets us offer "Add as
      // physical copy" against the existing mould instead of hard error.
      // Read from `originalDetail` (set by the App.js interceptor before
      // it flattens) so we still see the structured object even when
      // the interceptor has coerced `detail` to a string.
      const status = e.response?.status;
      const d = e.originalDetail ?? e.response?.data?.detail;
      if (status === 409 && d && typeof d === "object" && d.code === "MOULD_CODE_EXISTS" && d.suggest_add_copy) {
        setDup409({ existing: d.existing_mould, attemptedPayload: form });
        return;
      }
      const msg = (d && typeof d === "object" ? (d.message || d.detail || JSON.stringify(d)) : d) || e.message;
      toast.error(`Save failed: ${msg}`);
    }
  };

  // ---------------- Family Sub-Moulds ----------------
  const openSubMould = (parent) => {
    setShowSubMould(parent);
    setSubMouldForm({
      mould_code: "", part_name: "",
      per_unit_weight_g: "",
      mb_ratio_pct: parent.mb_ratio_pct || "",
      cavities: 1,
      wastage_polymer: parent.wastage_polymer || 1.02,
      wastage_mb: parent.wastage_mb || 1.00,
    });
  };
  const saveSubMould = async () => {
    if (!showSubMould) return;
    try {
      const suffix = subMouldForm.mould_code.trim();
      if (!suffix) { toast.error("Sub-mould suffix required (e.g. 1, 2A)"); return; }
      await axios.post(`${API}/moulds/${showSubMould.id}/sub-moulds`, {
        mould_code: suffix,
        part_name: subMouldForm.part_name || "",
        per_unit_weight_g: Number(subMouldForm.per_unit_weight_g) || 0,
        mb_ratio_pct: Number(subMouldForm.mb_ratio_pct) || 0,
        cavities: Number(subMouldForm.cavities) || 1,
        wastage_polymer: Number(subMouldForm.wastage_polymer) || 1.02,
        wastage_mb: Number(subMouldForm.wastage_mb) || 1.00,
        polymer_components: [],  // inherited from parent
      }, auth());
      toast.success(`Sub-mould ${showSubMould.mould_code}/${suffix} created`);
      setShowSubMould(null);
      loadAll();
    } catch (e) {
      const d = e.response?.data?.detail;
      const msg = (d && typeof d === "object" ? (d.message || JSON.stringify(d)) : d) || e.message;
      toast.error(`Create sub-mould failed: ${msg}`);
    }
  };

  // ---------------- Re-derive INP BOMs (Feb 2026) ----------------
  // Manual trigger that rebuilds every linked INP's rm_bom for this
  // mould. Reuses the auto-cascade renderer semantics — same
  // `cascade_id / rewritten / skipped / warnings` shape — so the
  // toast surface below mirrors the one used by `saveMould`.
  const reDeriveInpBoms = async (m) => {
    if (!m?.id) return;
    if (!window.confirm(
      `Re-derive rm_bom for every INP linked to ${m.mould_code}? ` +
      `This snapshots the prior BOMs (undo-able) and rewrites the ` +
      `polymer + MB rows from the current mould master.`
    )) return;
    try {
      const r = await axios.post(
        `${API}/moulds/${m.id}/re-derive-inp-boms`, {}, auth(),
      );
      const c = r.data || {};
      if (c.rewritten > 0) {
        toast.success(
          `Re-derived BOMs for ${c.rewritten} INP(s)` +
          (c.skipped ? `, skipped ${c.skipped}` : "") +
          (c.warnings_total ? `, ${c.warnings_total} warning(s)` : "") +
          `. Undo id: ${(c.cascade_id || "").slice(0, 8)}\u2026`
        );
      } else {
        toast.info(
          `No BOMs rewritten for ${m.mould_code}` +
          (c.skipped ? ` (skipped ${c.skipped})` : "") +
          `. Verify the mould has ACTIVE linked INPs with a resolved colourant.`
        );
      }
    } catch (e) {
      const d = e.response?.data?.detail;
      const msg = (d && typeof d === "object" ? (d.message || JSON.stringify(d)) : d) || e.message;
      toast.error(`Re-derive failed: ${msg}`);
    }
  };



  // ---------------- Physical Copies ----------------
  const openCopies = (m) => {
    setShowCopies(m);
    setCopyForm({ location_type: "BRANCH", branch_id: "", vendor_id: "", note: "" });
    setCopyMoveState(null);
  };
  const addCopy = async () => {
    if (!showCopies) return;
    try {
      const payload = {
        location_type: copyForm.location_type,
        branch_id: copyForm.location_type === "BRANCH" ? (copyForm.branch_id || null) : null,
        vendor_id: copyForm.location_type === "VENDOR" ? (copyForm.vendor_id || null) : null,
        note: copyForm.note || "",
      };
      await axios.post(`${API}/moulds/${showCopies.id}/copies`, payload, auth());
      toast.success("Physical copy added");
      setCopyForm({ location_type: "BRANCH", branch_id: "", vendor_id: "", note: "" });
      // Refresh from server so `physical_copies` reflects the new entry
      const r = await axios.get(`${API}/moulds/${showCopies.id}`, auth());
      setShowCopies(r.data);
      loadAll();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(`Add copy failed: ${(d && typeof d === "object" ? d.message : d) || e.message}`);
    }
  };
  const dup409AddCopy = async () => {
    // Add a copy against the existing mould with the same location fields
    // the user just tried to create with. Non-destructive; user can also
    // click Cancel to just close the dialog.
    if (!dup409) return;
    try {
      const p = dup409.attemptedPayload;
      await axios.post(`${API}/moulds/${dup409.existing.id}/copies`, {
        location_type: p.location_type,
        branch_id: p.location_type === "BRANCH" ? (p.branch_id || null) : null,
        vendor_id: p.location_type === "VENDOR" ? (p.vendor_id || null) : null,
        note: p.location_note || "",
      }, auth());
      toast.success(`Physical copy added to ${dup409.existing.mould_code}`);
      setDup409(null);
      setShowDialog(false);
      loadAll();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(`Add copy failed: ${(d && typeof d === "object" ? d.message : d) || e.message}`);
    }
  };
  const moveCopy = async () => {
    if (!showCopies || !copyMoveState) return;
    try {
      await axios.put(
        `${API}/moulds/${showCopies.id}/copies/${copyMoveState.copy_id}/location`,
        {
          location_type: copyMoveState.location_type,
          branch_id: copyMoveState.location_type === "BRANCH" ? (copyMoveState.branch_id || null) : null,
          vendor_id: copyMoveState.location_type === "VENDOR" ? (copyMoveState.vendor_id || null) : null,
          note: copyMoveState.note || "",
        },
        auth(),
      );
      toast.success("Copy location updated");
      const r = await axios.get(`${API}/moulds/${showCopies.id}`, auth());
      setShowCopies(r.data);
      setCopyMoveState(null);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(`Move failed: ${(d && typeof d === "object" ? d.message : d) || e.message}`);
    }
  };

  // ---------------- Documents ----------------
  const openDocs = async (m) => {
    setShowDocs(m);
    setDocs([]);
    setDocFile(null);
    setDocType("OTHER");
    setDocsLoading(true);
    try {
      const r = await axios.get(`${API}/moulds/${m.id}/documents`, auth());
      setDocs(r.data || []);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(`Load docs failed: ${(d && typeof d === "object" ? d.message : d) || e.message}`);
    } finally {
      setDocsLoading(false);
    }
  };
  const uploadDoc = async () => {
    if (!showDocs || !docFile) return;
    setDocUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", docFile);
      await axios.post(
        `${API}/moulds/${showDocs.id}/documents?doc_type=${docType}`,
        fd,
        { ...auth(), headers: { ...auth().headers, "Content-Type": "multipart/form-data" } },
      );
      toast.success("Document uploaded");
      setDocFile(null);
      const r = await axios.get(`${API}/moulds/${showDocs.id}/documents`, auth());
      setDocs(r.data || []);
      loadAll();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(`Upload failed: ${(d && typeof d === "object" ? d.message : d) || e.message}`);
    } finally {
      setDocUploading(false);
    }
  };
  const downloadDoc = async (doc) => {
    try {
      const r = await axios.get(
        `${API}/moulds/${showDocs.id}/documents/${doc.doc_id}`,
        { ...auth(), responseType: "blob" },
      );
      saveAs(new Blob([r.data], { type: doc.content_type || "application/octet-stream" }), doc.filename || `${doc.doc_id}.bin`);
    } catch (e) {
      toast.error(`Download failed: ${e.response?.data?.detail || e.message}`);
    }
  };
  const deleteDoc = async (doc) => {
    if (!window.confirm(`Delete document "${doc.filename}"?`)) return;
    try {
      await axios.delete(`${API}/moulds/${showDocs.id}/documents/${doc.doc_id}`, auth());
      const r = await axios.get(`${API}/moulds/${showDocs.id}/documents`, auth());
      setDocs(r.data || []);
      loadAll();
    } catch (e) {
      toast.error(`Delete failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  const deleteMould = async (m) => {
    try {
      await axios.delete(`${API}/moulds/${m.id}`, auth());
      toast.success("Mould deleted");
      setDeleteConfirm(null);
      loadAll();
    } catch (e) {
      toast.error(`Delete failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  const [auditActiveOnly, setAuditActiveOnly] = useState(false);  // default: include INACTIVE so count matches RM Repository

  // ---------------- Variance Audit ----------------
  const fetchAudit = async (activeOnly) => {
    setAuditLoading(true);
    try {
      const r = await axios.get(`${API}/moulds/audit-existing-inps?active_only=${activeOnly}`, auth());
      setAuditData(r.data);
    } catch (e) {
      toast.error(`Audit failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setAuditLoading(false);
    }
  };

  const openAudit = async () => {
    setShowAudit(true);
    await fetchAudit(auditActiveOnly);
  };

  const downloadAudit = async () => {
    try {
      const r = await axios.get(`${API}/moulds/audit-existing-inps/export?active_only=${auditActiveOnly}`, { ...auth(), responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `mould_master_audit_${Date.now()}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Audit downloaded");
    } catch (e) {
      toast.error(`Download failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  const runBackfillPartNames = async () => {
    try {
      const r = await axios.post(`${API}/moulds/backfill-part-names`, {}, auth());
      toast.success(`Backfilled part names on ${r.data.updated} moulds (skipped ${r.data.skipped})`);
      loadAll();
    } catch (e) {
      toast.error(`Backfill failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  // ---------------- Bulk Download ----------------
  // Feb 2026: Server-driven export so the schema stays lockstep with
  // the Bulk Upload Template. Roundtrip-safe — downloading and
  // re-uploading with no edits results in zero changes on dry-run.
  const exportAllMoulds = async () => {
    try {
      const params = locationFilter !== "ALL" ? `?location_type=${locationFilter}` : "";
      const r = await axios.get(`${API}/moulds/export${params}`, {
        ...auth(), responseType: "blob",
      });
      const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      saveAs(new Blob([r.data]), `mould_master_${ts}.xlsx`);
      toast.success("Export downloaded");
    } catch (e) {
      toast.error(`Export failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  // ---------------- Sub-Mould Bulk Upload + Family Parent Cleanup (Feb 2026) ----------------
  const downloadSubBulkTemplate = async () => {
    try {
      const r = await axios.get(`${API}/moulds/sub-mould-bulk-template`, { ...auth(), responseType: "blob" });
      const fname = `sub_mould_bulk_template_${new Date().toISOString().slice(0, 10).replace(/-/g, "")}.xlsx`;
      saveAs(new Blob([r.data]), fname);
      toast.success("Sub-mould template downloaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to download template");
    }
  };

  const submitSubBulkUpload = async () => {
    if (!subBulkFile) { toast.error("Pick a file first"); return; }
    setSubBulkBusy(true); setSubBulkResult(null);
    try {
      const fd = new FormData();
      fd.append("file", subBulkFile);
      const r = await axios.post(`${API}/moulds/sub-mould-bulk-upload?dry_run=${subBulkDryRun}`, fd, {
        ...auth(),
        headers: { ...auth().headers, "Content-Type": "multipart/form-data" },
      });
      setSubBulkResult(r.data);
      const s = r.data.summary;
      if (subBulkDryRun) {
        toast(`Dry-run: ${s.created} would create, ${s.updated} would update, ${s.errors} errors`);
      } else {
        toast.success(`Upload complete: ${s.created} created, ${s.updated} updated, ${s.errors} errors`);
        loadAll();
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : detail?.message || e.message;
      toast.error(`Sub-mould upload failed: ${msg}`);
    } finally {
      setSubBulkBusy(false);
    }
  };

  const runCleanupAll = async () => {
    setCleanupBusy(true);
    try {
      const r = await axios.post(`${API}/moulds/cleanup-family-parents-all`, null, auth());
      setShowCleanupResult(r.data);
      const c = r.data.cleaned?.length || 0;
      const s = r.data.skipped?.length || 0;
      const n = r.data.no_change?.length || 0;
      toast.success(`Cleanup complete: ${c} cleared, ${s} skipped, ${n} unchanged`);
      loadAll();
    } catch (e) {
      toast.error(`Cleanup failed: ${e.response?.data?.detail?.message || e.response?.data?.detail || e.message}`);
    } finally {
      setCleanupBusy(false);
    }
  };

  // Feb 2026 — Adopt orphan slash-coded sub-moulds. Two-step UX:
  //   1) Dry-run to compute the plan and show counts in a dialog.
  //   2) User confirms → apply (dry_run=false).
  // Endpoint is idempotent, so an accidental double-apply is harmless.
  const runAdoptOrphansDryRun = async () => {
    setAdoptBusy(true);
    setAdoptApplied(null);
    try {
      const r = await axios.post(
        `${API}/moulds/adopt-orphan-sub-moulds?dry_run=true`, null, auth());
      setAdoptPlan(r.data);
      const s = r.data.summary;
      const total = s.headers_created + s.headers_reactivated + s.children_linked;
      if (total === 0 && s.skipped === 0) {
        toast("No orphan sub-moulds to adopt — library is already tidy.");
      } else {
        toast(`Dry-run: ${s.children_linked} children under `
          + `${s.headers_created + s.headers_reactivated + s.headers_reused} `
          + `family header(s); ${s.skipped} skipped.`);
      }
    } catch (e) {
      toast.error(`Adopt (dry-run) failed: `
        + `${e.response?.data?.detail?.message || e.response?.data?.detail || e.message}`);
    } finally {
      setAdoptBusy(false);
    }
  };

  const runAdoptOrphansApply = async () => {
    setAdoptBusy(true);
    try {
      const r = await axios.post(
        `${API}/moulds/adopt-orphan-sub-moulds?dry_run=false`, null, auth());
      setAdoptApplied(r.data);
      const s = r.data.summary;
      toast.success(
        `Adopted: created ${s.headers_created}, reactivated `
        + `${s.headers_reactivated}, reused ${s.headers_reused}, linked `
        + `${s.children_linked} children, skipped ${s.skipped}.`);
      loadAll();
    } catch (e) {
      toast.error(`Adopt failed: `
        + `${e.response?.data?.detail?.message || e.response?.data?.detail || e.message}`);
    } finally {
      setAdoptBusy(false);
    }
  };

  // ---------------- Bulk Upload ----------------
  const submitUpload = async () => {
    if (!uploadFile) { toast.error("Pick a file first"); return; }
    setUploadBusy(true);
    setUploadResult(null);
    try {
      const fd = new FormData();
      fd.append("file", uploadFile);
      const r = await axios.post(`${API}/moulds/bulk-upload?dry_run=${uploadDryRun}`, fd, {
        ...auth(),
        headers: { ...auth().headers, "Content-Type": "multipart/form-data" },
      });
      setUploadResult(r.data);
      if (!uploadDryRun) {
        toast.success(`Upload complete: ${r.data.summary.upserted} upserted, ${r.data.summary.inps_linked} INPs linked`);
        loadAll();
      } else {
        toast(`Dry-run: ${r.data.summary.upserted} would be upserted`);
      }
    } catch (e) {
      toast.error(`Upload failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setUploadBusy(false);
    }
  };

  // ---------------- UI ----------------
  return (
    <div className="container mx-auto p-6 space-y-6" data-testid="moulds-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Factory className="w-6 h-6" /> Mould Master</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Physical moulds drive INP BOMs. Polymer + weight + MB-ratio are set here once and cascaded to every INP that uses the mould.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={openAudit} data-testid="audit-btn"
            title="Audit INP data quality against the mould master (33-col report)">
            <AlertTriangle className="w-4 h-4 mr-2" /> Variance Audit
          </Button>
          <Button
            variant="outline"
            onClick={exportAllMoulds}
            data-testid="export-all-btn"
            title="Download current mould master data — same shape as the Bulk Upload Template (roundtrip-safe)"
          >
            <Download className="w-4 h-4 mr-2" /> Export All Moulds
          </Button>
          <Button
            variant="outline"
            onClick={async () => {
              try {
                const r = await axios.get(`${API}/moulds/bulk-template`, { ...auth(), responseType: "blob" });
                const fname = `mould_master_template_${new Date().toISOString().slice(0, 10).replace(/-/g, "")}.xlsx`;
                saveAs(new Blob([r.data]), fname);
                toast.success("Template downloaded");
              } catch (e) {
                toast.error(e.response?.data?.detail || "Failed to download template");
              }
            }}
            data-testid="download-template-btn"
            title="Blank data-entry template that mirrors the Add Mould dialog fields"
          >
            <Download className="w-4 h-4 mr-2" /> Bulk Upload Template
          </Button>
          <Button variant="outline" onClick={() => { setShowUpload(true); setUploadResult(null); setUploadFile(null); }} data-testid="upload-btn">
            <Upload className="w-4 h-4 mr-2" /> Bulk Upload
          </Button>
          <Button
            variant="outline"
            onClick={() => { setShowSubBulk(true); setSubBulkResult(null); setSubBulkFile(null); }}
            data-testid="sub-mould-bulk-btn"
            title="Bulk create/update sub-moulds of family parents from Excel"
          >
            <Upload className="w-4 h-4 mr-2" /> Sub-Mould Bulk
          </Button>
          <Button
            variant="outline"
            onClick={runCleanupAll}
            disabled={cleanupBusy}
            data-testid="cleanup-family-parents-btn"
            title="Clear part-level fields on family parents whose INPs are all retagged to sub-moulds"
          >
            <AlertTriangle className="w-4 h-4 mr-2" /> {cleanupBusy ? "Cleaning..." : "Cleanup Family Parents"}
          </Button>
          <Button
            variant="outline"
            onClick={runAdoptOrphansDryRun}
            disabled={adoptBusy}
            data-testid="adopt-orphan-sub-moulds-btn"
            title="Link flat 'PARENT/N' sub-moulds (from legacy Bulk Upload) into proper mould families"
          >
            <AlertTriangle className="w-4 h-4 mr-2" /> {adoptBusy ? "Scanning..." : "Adopt Orphan Sub-Moulds"}
          </Button>
          <Button onClick={openCreate} data-testid="add-mould-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Mould
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-3 flex-wrap items-center">
        <div className="relative flex-1 min-w-64">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search mould code / part / model / location…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="mould-search"
          />
        </div>
        <Select value={locationFilter} onValueChange={setLocationFilter}>
          <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All locations</SelectItem>
            <SelectItem value="BRANCH">In-house (Branch)</SelectItem>
            <SelectItem value="VENDOR">Job-work (Vendor)</SelectItem>
          </SelectContent>
        </Select>
        <div className="text-sm text-muted-foreground">{filtered.length} / {moulds.length}</div>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="text-center text-muted-foreground py-12">Loading moulds…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center text-muted-foreground py-12 border-2 border-dashed rounded-lg">
          No moulds yet. Run <b>Variance Audit</b> to seed the master from existing INPs, or click <b>Add Mould</b>.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filtered.map(m => (
            <div key={m.id} className="border rounded-lg bg-white shadow-sm overflow-hidden hover:shadow-md transition" data-testid={`mould-card-${m.mould_code}`}>
              <div className="aspect-video bg-slate-100 flex items-center justify-center relative">
                {imageBlobUrls[m.id] ? (
                  <img src={imageBlobUrls[m.id]} alt={m.mould_code} className="w-full h-full object-cover" />
                ) : (
                  <ImageIcon className="w-12 h-12 text-slate-300" />
                )}
                <Badge className="absolute top-2 left-2 bg-slate-900/80 text-white font-mono text-xs">
                  {m.mould_code}
                </Badge>
              </div>
              <div className="p-3 space-y-2">
                <div className="flex justify-between items-start gap-2">
                  <div className="text-sm font-medium leading-tight">{m.part_name || <span className="text-muted-foreground italic">No part name</span>}</div>
                  <div className="flex gap-1">
                    <Button size="icon" variant="ghost" onClick={() => openEdit(m)} data-testid={`edit-${m.mould_code}`} title="Edit"><Pencil className="w-3.5 h-3.5" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => setDeleteConfirm(m)} data-testid={`delete-${m.mould_code}`} title="Delete"><Trash2 className="w-3.5 h-3.5 text-red-500" /></Button>
                  </div>
                </div>
                <div className="text-xs text-muted-foreground space-y-1">
                  <div>
                    {(m.polymer_components && m.polymer_components.length > 1) ? (
                      <span>Blend: <span className="font-mono text-slate-700">{m.polymer_components.map(c => `${c.polymer_rm_id}@${c.percentage}%`).join(" + ")}</span></span>
                    ) : (
                      <span>Polymer: <span className="font-mono text-slate-700">{m.polymer_rm_id || (m.polymer_components?.[0]?.polymer_rm_id) || "—"}</span></span>
                    )}
                  </div>
                  <div>Weight: <span className="text-slate-700">{m.per_unit_weight_g || 0} g</span> · MB %: <span className="text-slate-700">{m.mb_ratio_pct ?? 0}%</span></div>
                  {(m.cycle_time_sec > 0 || (m.compatible_machines && m.compatible_machines.length > 0)) && (
                    <div data-testid={`card-ops-${m.mould_code}`}>
                      {m.cycle_time_sec > 0 && (
                        <>Cycle: <span className="text-slate-700">{m.cycle_time_sec}s</span></>
                      )}
                      {m.cycle_time_sec > 0 && m.compatible_machines && m.compatible_machines.length > 0 && (
                        <span className="mx-1">·</span>
                      )}
                      {m.compatible_machines && m.compatible_machines.length > 0 && (
                        <>Machines: <span className="text-slate-700">{m.compatible_machines.join(", ")}</span></>
                      )}
                    </div>
                  )}
                  {(m.mould_length_mm > 0 || m.mould_breadth_mm > 0 || m.mould_height_mm > 0 || m.mould_weight_kg > 0) && (
                    <div data-testid={`card-dims-${m.mould_code}`}>
                      {(m.mould_length_mm > 0 || m.mould_breadth_mm > 0 || m.mould_height_mm > 0) && (
                        <>LBH: <span className="text-slate-700">
                          {m.mould_length_mm || 0}×{m.mould_breadth_mm || 0}×{m.mould_height_mm || 0} mm
                        </span></>
                      )}
                      {(m.mould_length_mm > 0 || m.mould_breadth_mm > 0 || m.mould_height_mm > 0) && m.mould_weight_kg > 0 && (
                        <span className="mx-1">·</span>
                      )}
                      {m.mould_weight_kg > 0 && (
                        <>Weight: <span className="text-slate-700">{m.mould_weight_kg} kg</span></>
                      )}
                    </div>
                  )}
                  <div className="flex items-center gap-1">
                    {m.location_type === "VENDOR" ? <Truck className="w-3 h-3" /> : <MapPin className="w-3 h-3" />}
                    {m.location_type === "VENDOR" ? <span className="text-amber-700">Job-work · {m.vendor_name || "—"}</span> : <span>{m.branch_name || "—"}</span>}
                  </div>
                </div>

                {/* Family sub-moulds — chips row (Feb 2026 Phase B) */}
                {(subMouldsByParent[m.id] || []).length > 0 && (
                  <div className="pt-2 border-t" data-testid={`family-chips-${m.mould_code}`}>
                    <div className="flex items-center gap-1 text-[11px] font-semibold text-slate-600 mb-1">
                      <GitFork className="w-3 h-3" /> Family · {(subMouldsByParent[m.id] || []).length} sub-mould(s)
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {(subMouldsByParent[m.id] || []).map(sm => (
                        <button
                          key={sm.id}
                          onClick={() => openEdit(sm)}
                          className="text-[10px] font-mono px-2 py-0.5 rounded bg-indigo-50 text-indigo-800 border border-indigo-200 hover:bg-indigo-100"
                          data-testid={`family-chip-${sm.mould_code}`}
                          title={`${sm.part_name || ""} · ${sm.per_unit_weight_g || 0}g`}
                        >
                          {sm.mould_code.split("/").pop()}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Phase B action row — Sub-mould / Copies / Docs */}
                <div className="pt-2 border-t flex flex-wrap gap-1" data-testid={`card-actions-${m.mould_code}`}>
                  <Button
                    size="sm" variant="outline"
                    className="h-7 text-[11px] flex-1"
                    onClick={() => openSubMould(m)}
                    data-testid={`add-submould-${m.mould_code}`}
                    title="Mint a sub-mould that inherits polymer + location from this parent"
                  >
                    <GitFork className="w-3 h-3 mr-1" /> Sub-mould
                  </Button>
                  <Button
                    size="sm" variant="outline"
                    className="h-7 text-[11px] flex-1"
                    onClick={() => openCopies(m)}
                    data-testid={`copies-btn-${m.mould_code}`}
                    title="Track additional physical copies of this mould"
                  >
                    <CopyIcon className="w-3 h-3 mr-1" /> Copies
                    {(m.physical_copies?.length || 0) > 0 && (
                      <Badge className="ml-1 h-4 px-1 text-[9px] bg-slate-200 text-slate-800">{m.physical_copies.length}</Badge>
                    )}
                  </Button>
                  <Button
                    size="sm" variant="outline"
                    className="h-7 text-[11px] flex-1"
                    onClick={() => openDocs(m)}
                    data-testid={`docs-btn-${m.mould_code}`}
                    title="Attach drawings, trial reports, maintenance logs"
                  >
                    <FileText className="w-3 h-3 mr-1" /> Docs
                    {(m.documents?.length || 0) > 0 && (
                      <Badge className="ml-1 h-4 px-1 text-[9px] bg-slate-200 text-slate-800">{m.documents.length}</Badge>
                    )}
                  </Button>
                  <Button
                    size="sm" variant="outline"
                    className="h-7 text-[11px] flex-1"
                    onClick={() => reDeriveInpBoms(m)}
                    data-testid={`rederive-boms-${m.mould_code}`}
                    title="Rebuild rm_bom for every INP linked to this mould (snapshot + undo)"
                  >
                    <RefreshCw className="w-3 h-3 mr-1" /> Re-derive BOMs
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="mould-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Mould" : "Add Mould"}</DialogTitle>
            <DialogDescription>{editing ? `Editing ${editing.mould_code}` : "Create a new physical mould. The values here become the canonical source for every INP that uses this mould code."}</DialogDescription>
            {editing?.is_sub_mould && (
              <div className="mt-2 rounded-md bg-indigo-50 border border-indigo-200 p-2 text-xs text-indigo-900 flex items-start gap-2" data-testid="submould-inherit-banner">
                <GitFork className="w-4 h-4 flex-none mt-0.5" />
                <div>
                  <b>Sub-mould of {editing?.parent_mould_id ? (moulds.find(x => x.id === editing.parent_mould_id)?.mould_code || "parent") : "parent"}.</b>{" "}
                  Polymer blend + location are inherited from the parent and can&apos;t be edited here — change them on the parent mould instead. Weight, MB%, cavities, dimensions and part name are editable per sub-mould.
                </div>
              </div>
            )}
          </DialogHeader>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Mould Code *</Label>
              <Input
                value={form.mould_code}
                onChange={(e) => setForm({ ...form, mould_code: e.target.value })}
                disabled={!!editing}
                placeholder="e.g. SC1-15"
                className="font-mono"
                data-testid="mould-code-input"
              />
            </div>
            <div>
              <Label>Part Name</Label>
              <Input value={form.part_name} onChange={(e) => setForm({ ...form, part_name: e.target.value })} placeholder="e.g. Base" />
            </div>
            <div className="col-span-2">
              <Label className="flex items-center justify-between">
                <span>Polymer Blend (1–4 components, sum to 100%) *</span>
                <span className={`text-xs font-mono ${Math.abs(blendPctSum - 100) <= 0.5 ? "text-emerald-600" : "text-red-600"}`}>
                  Total: {blendPctSum.toFixed(2)}%
                </span>
              </Label>
              <div className="space-y-2 mt-1">
                {form.polymer_components.map((c, i) => (
                  <div key={i} className="flex gap-2 items-center" data-testid={`blend-row-${i}`}>
                    <Select value={c.polymer_rm_id} onValueChange={(v) => updateBlendRow(i, "polymer_rm_id", v)} disabled={editing?.is_sub_mould}>
                      <SelectTrigger className="flex-1"><SelectValue placeholder="Pick a POLY" /></SelectTrigger>
                      <SelectContent className="max-h-64">
                        {polymers.map(p => <SelectItem key={p.rm_id} value={p.rm_id}>{p.rm_id} — {p.description}</SelectItem>)}
                        {polymers.length === 0 && <div className="p-2 text-xs text-muted-foreground">No POLY RMs found</div>}
                      </SelectContent>
                    </Select>
                    <Input
                      type="number" step="0.01" min="0" max="100"
                      value={c.percentage}
                      onChange={(e) => updateBlendRow(i, "percentage", e.target.value)}
                      placeholder="%"
                      className="w-24 text-right"
                      data-testid={`blend-pct-${i}`}
                      disabled={editing?.is_sub_mould}
                    />
                    <span className="text-xs text-muted-foreground w-3">%</span>
                    <Button size="icon" variant="ghost" onClick={() => removeBlendRow(i)} disabled={form.polymer_components.length === 1}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))}
                <Button size="sm" variant="outline" onClick={addBlendRow} disabled={form.polymer_components.length >= 4} data-testid="add-blend-row">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add polymer component
                </Button>
              </div>
            </div>
            <div>
              <Label>Per-unit Weight (g) *</Label>
              <Input type="number" step="0.01" value={form.per_unit_weight_g} onChange={(e) => setForm({ ...form, per_unit_weight_g: e.target.value })} placeholder="496" />
            </div>
            <div>
              <Label>MB Ratio (%) *</Label>
              <Input type="number" step="0.01" value={form.mb_ratio_pct} onChange={(e) => setForm({ ...form, mb_ratio_pct: e.target.value })} placeholder="1.0" />
            </div>
            <div>
              <Label>Cavities</Label>
              <Input type="number" min="1" value={form.cavities} onChange={(e) => setForm({ ...form, cavities: e.target.value })} />
            </div>
            <div>
              <Label>Wastage Polymer</Label>
              <Input type="number" step="0.001" value={form.wastage_polymer} onChange={(e) => setForm({ ...form, wastage_polymer: e.target.value })} />
            </div>
            <div>
              <Label>Wastage MB</Label>
              <Input type="number" step="0.001" value={form.wastage_mb} onChange={(e) => setForm({ ...form, wastage_mb: e.target.value })} />
            </div>

            {/* Ops & logistics (Feb 2026 — informational, not cascaded to BOMs) */}
            <div className="col-span-2 border-t pt-4 mt-2">
              <Label className="text-sm font-semibold">Operations & Logistics</Label>
              <p className="text-xs text-muted-foreground mt-1">Informational — used on the mould card. Not cascaded to INP BOMs.</p>
            </div>
            <div>
              <Label>Cycle Time (sec)</Label>
              <Input
                type="number" step="0.1" min="0"
                value={form.cycle_time_sec}
                onChange={(e) => setForm({ ...form, cycle_time_sec: e.target.value })}
                placeholder="42.5"
                data-testid="cycle-time-input"
              />
            </div>
            <div>
              <Label>Compatible Machines</Label>
              <Input
                value={form.compatible_machines}
                onChange={(e) => setForm({ ...form, compatible_machines: e.target.value })}
                placeholder="150T, 250T, 400T"
                data-testid="compatible-machines-input"
              />
              <p className="text-[10px] text-muted-foreground mt-1">Comma-separated tonnage / machine codes.</p>
            </div>
            <div>
              <Label>Mould Length (mm)</Label>
              <Input
                type="number" step="1" min="0"
                value={form.mould_length_mm}
                onChange={(e) => setForm({ ...form, mould_length_mm: e.target.value })}
                placeholder="600"
                data-testid="mould-length-input"
              />
            </div>
            <div>
              <Label>Mould Breadth (mm)</Label>
              <Input
                type="number" step="1" min="0"
                value={form.mould_breadth_mm}
                onChange={(e) => setForm({ ...form, mould_breadth_mm: e.target.value })}
                placeholder="400"
                data-testid="mould-breadth-input"
              />
            </div>
            <div>
              <Label>Mould Height (mm)</Label>
              <Input
                type="number" step="1" min="0"
                value={form.mould_height_mm}
                onChange={(e) => setForm({ ...form, mould_height_mm: e.target.value })}
                placeholder="350"
                data-testid="mould-height-input"
              />
            </div>
            <div>
              <Label>Mould Weight (kg)</Label>
              <Input
                type="number" step="0.1" min="0"
                value={form.mould_weight_kg}
                onChange={(e) => setForm({ ...form, mould_weight_kg: e.target.value })}
                placeholder="1250"
                data-testid="mould-weight-input"
              />
            </div>

            {/* Location */}
            <div className="col-span-2 border-t pt-4 mt-2">
              <Label className="text-sm font-semibold">Mould Location</Label>
              <div className="flex gap-2 mt-2">
                <Button
                  type="button"
                  variant={form.location_type === "BRANCH" ? "default" : "outline"}
                  onClick={() => setForm({ ...form, location_type: "BRANCH", vendor_id: "" })}
                  size="sm"
                ><MapPin className="w-4 h-4 mr-1" /> In-house (Branch)</Button>
                <Button
                  type="button"
                  variant={form.location_type === "VENDOR" ? "default" : "outline"}
                  onClick={() => setForm({ ...form, location_type: "VENDOR", branch_id: "" })}
                  size="sm"
                ><Truck className="w-4 h-4 mr-1" /> Other Location (Vendor / Job-work)</Button>
              </div>
              {form.location_type === "BRANCH" ? (
                <div className="mt-3">
                  <Label className="text-xs">Branch *</Label>
                  <Select value={form.branch_id} onValueChange={(v) => setForm({ ...form, branch_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Pick a branch" /></SelectTrigger>
                    <SelectContent>
                      {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div className="mt-3">
                  <Label className="text-xs">Vendor (Job-work) *</Label>
                  <Select value={form.vendor_id} onValueChange={(v) => setForm({ ...form, vendor_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Pick a vendor" /></SelectTrigger>
                    <SelectContent className="max-h-64">
                      {vendors.map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="mt-3">
                <Label className="text-xs">Note (optional)</Label>
                <Input value={form.location_note} onChange={(e) => setForm({ ...form, location_note: e.target.value })} placeholder="e.g. Moved to vendor on 1-Feb" />
              </div>
            </div>

            {/* Image upload */}
            <div className="col-span-2 border-t pt-4">
              <Label className="text-sm font-semibold flex items-center gap-2"><ImageIcon className="w-4 h-4" /> Mould Image</Label>
              <div className="mt-2 flex items-center gap-4">
                {editing && imageBlobUrls[editing.id] && (
                  <img src={imageBlobUrls[editing.id]} alt="current" className="w-24 h-24 object-cover rounded border" />
                )}
                <Input type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={(e) => setImageFile(e.target.files?.[0] || null)} />
              </div>
              {imageFile && <div className="mt-1 text-xs text-muted-foreground">Selected: {imageFile.name} ({(imageFile.size/1024).toFixed(0)} KB)</div>}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={saveMould} disabled={!form.mould_code || (!editing?.is_sub_mould && (!blendIsValid || (form.location_type === "BRANCH" ? !form.branch_id : !form.vendor_id)))} data-testid="save-mould-btn">
              {editing ? "Save Changes" : "Create Mould"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Variance Audit Dialog */}
      <Dialog open={showAudit} onOpenChange={setShowAudit}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="audit-dialog">
          <DialogHeader>
            <DialogTitle>Variance Audit — Existing INPs</DialogTitle>
            <DialogDescription>
              Scans every INP, groups by mould_code, and flags drift in weight / polymer / MB-ratio. Download to review, correct, and re-upload.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 text-sm">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={auditActiveOnly}
                onChange={async (e) => { setAuditActiveOnly(e.target.checked); await fetchAudit(e.target.checked); }}
                data-testid="audit-active-only"
              />
              Show ACTIVE INPs only (default: include INACTIVE so count matches RM Repository)
            </label>
          </div>
          {auditLoading ? (
            <div className="py-8 text-center text-muted-foreground">Scanning…</div>
          ) : !auditData ? null : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-2">
                {[
                  ["Total moulds", auditData.summary.total_moulds, "bg-slate-100"],
                  ["Need review", auditData.summary.moulds_needing_review, "bg-rose-100 text-rose-800"],
                  ["Weight variance", auditData.summary.weight_variance_count, "bg-amber-100 text-amber-800"],
                  ["Polymer variance", auditData.summary.polymer_variance_count, "bg-amber-100 text-amber-800"],
                  ["MB-ratio variance", auditData.summary.mb_ratio_variance_count, "bg-amber-100 text-amber-800"],
                  ["No BOM data", auditData.summary.moulds_with_no_bom, "bg-slate-100"],
                  [`INPs (active)`, auditData.summary.active_inps_covered ?? auditData.summary.total_inps_covered, "bg-blue-100 text-blue-800"],
                  [`INPs (inactive)`, auditData.summary.inactive_inps_covered ?? 0, "bg-slate-100"],
                ].map(([k, v, cls]) => (
                  <div key={k} className={`rounded-md p-3 ${cls}`}>
                    <div className="text-xs uppercase tracking-wide opacity-70">{k}</div>
                    <div className="text-xl font-bold">{v}</div>
                  </div>
                ))}
              </div>

              <div className="mt-4 border rounded-md max-h-80 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      <th className="text-left p-2">Mould</th>
                      <th className="text-right p-2">INPs</th>
                      <th className="text-left p-2">Weights</th>
                      <th className="text-left p-2">Polymers</th>
                      <th className="text-left p-2">MB %</th>
                      <th className="text-center p-2">Flag</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(auditData.moulds || []).filter(m => m.needs_review).slice(0, 50).map(m => (
                      <tr key={m.mould_code} className="border-t bg-rose-50/40">
                        <td className="p-2 font-mono">{m.mould_code}</td>
                        <td className="p-2 text-right">{m.inp_count}</td>
                        <td className="p-2">{(m.weight_distinct_values || []).join(", ")}</td>
                        <td className="p-2 font-mono">{(m.polymer_distinct || []).join(", ") || "—"}</td>
                        <td className="p-2">{(m.mb_ratio_distinct_values || []).join(", ") || "—"}</td>
                        <td className="p-2 text-center">⚠️</td>
                      </tr>
                    ))}
                    {auditData.summary.moulds_needing_review === 0 && (
                      <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">No drift found — all mould codes are internally consistent. 🎉</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAudit(false)}>Close</Button>
            <Button onClick={downloadAudit} data-testid="download-audit-btn">
              <Download className="w-4 h-4 mr-2" /> Download Excel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Upload Dialog */}
      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="upload-dialog">
          <DialogHeader>
            <DialogTitle>Bulk Upload Mould Master</DialogTitle>
            <DialogDescription>
              Upload a filled Bulk Upload Template (or an Export All Moulds file). The parser silently ignores any Model / CORRECTED_* / audit-report columns from legacy files. Every linked INP is stamped with its mould_id and its rm_bom is re-derived from the mould.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input type="file" accept=".xlsx,.xlsm" onChange={(e) => setUploadFile(e.target.files?.[0] || null)} />
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={uploadDryRun} onChange={(e) => setUploadDryRun(e.target.checked)} />
              Dry-run (validate without writing)
            </label>
            <Button onClick={submitUpload} disabled={!uploadFile || uploadBusy} className="w-full" data-testid="submit-upload-btn">
              {uploadBusy ? "Uploading…" : (uploadDryRun ? "Run Dry-Run" : "Upload & Apply")}
            </Button>

            {/* One-shot backfill for moulds uploaded before Part Name carry-through was fixed */}
            <div className="border-t pt-3 mt-3">
              <div className="text-xs font-semibold text-slate-700 mb-1">Quick fix</div>
              <div className="text-xs text-muted-foreground mb-2">
                If your existing moulds show &quot;No part name&quot;, run this once to derive part names from linked INPs.
              </div>
              <Button variant="outline" size="sm" onClick={runBackfillPartNames} data-testid="backfill-part-names-btn">
                Backfill Part Names from linked INPs
              </Button>
            </div>

            {uploadResult && (
              <div className="border rounded-md p-3 bg-slate-50 text-sm">
                <div className="font-semibold mb-2">Summary {uploadResult.dry_run && <Badge variant="outline">Dry-run</Badge>}</div>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div>Upserted: <b>{uploadResult.summary.upserted}</b></div>
                  <div>INPs linked: <b>{uploadResult.summary.inps_linked}</b></div>
                  <div>Errors: <b className="text-red-600">{uploadResult.summary.errors}</b></div>
                </div>
                <div className="mt-2 max-h-48 overflow-y-auto bg-white rounded border p-2 text-xs font-mono">
                  {uploadResult.receipt.slice(0, 50).map((r, i) => (
                    <div key={i} className={r.status === "ERROR" ? "text-red-600" : r.status === "OK_DRY_RUN" ? "text-blue-600" : "text-emerald-700"}>
                      [{r.status}] {r.mould_code} — {r.action || r.reason || ""}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete mould?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-mono">{deleteConfirm?.mould_code}</span> will be marked INACTIVE. Blocked if any active INP still references it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => deleteMould(deleteConfirm)} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* 409 Duplicate — offer to add as physical copy */}
      <AlertDialog open={!!dup409} onOpenChange={() => setDup409(null)}>
        <AlertDialogContent data-testid="dup409-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Mould code already exists</AlertDialogTitle>
            <AlertDialogDescription>
              A mould with code <span className="font-mono font-semibold">{dup409?.existing?.mould_code}</span> already exists
              ({dup409?.existing?.part_name || "no part name"} · {dup409?.existing?.physical_copy_count ?? 0} physical copy/copies).
              Would you like to add this as another physical copy of the existing mould instead? All BOMs stay tied to the existing code.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="dup409-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={dup409AddCopy}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="dup409-add-copy"
            >
              <CopyIcon className="w-4 h-4 mr-2" /> Add as physical copy
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Sub-Mould Dialog */}
      <Dialog open={!!showSubMould} onOpenChange={() => setShowSubMould(null)}>
        <DialogContent className="max-w-lg" data-testid="submould-dialog">
          <DialogHeader>
            <DialogTitle>Add Sub-Mould</DialogTitle>
            <DialogDescription>
              Sub-mould inherits <b>polymer blend + location</b> from parent
              <span className="font-mono ml-1">{showSubMould?.mould_code}</span>.
              You only set part-specific fields (weight, MB%, cavities).
              Full code becomes <span className="font-mono">{showSubMould?.mould_code}/{subMouldForm.mould_code || "…"}</span>.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Suffix *</Label>
              <Input
                value={subMouldForm.mould_code}
                onChange={(e) => setSubMouldForm({ ...subMouldForm, mould_code: e.target.value })}
                placeholder="e.g. 1 or 2A"
                className="font-mono"
                data-testid="submould-suffix"
              />
            </div>
            <div>
              <Label>Part Name</Label>
              <Input
                value={subMouldForm.part_name}
                onChange={(e) => setSubMouldForm({ ...subMouldForm, part_name: e.target.value })}
                placeholder="e.g. Base variant A"
                data-testid="submould-partname"
              />
            </div>
            <div>
              <Label>Per-unit Weight (g) *</Label>
              <Input
                type="number" step="0.01"
                value={subMouldForm.per_unit_weight_g}
                onChange={(e) => setSubMouldForm({ ...subMouldForm, per_unit_weight_g: e.target.value })}
                placeholder="e.g. 42.5"
                data-testid="submould-weight"
              />
            </div>
            <div>
              <Label>MB Ratio (%)</Label>
              <Input
                type="number" step="0.01"
                value={subMouldForm.mb_ratio_pct}
                onChange={(e) => setSubMouldForm({ ...subMouldForm, mb_ratio_pct: e.target.value })}
                placeholder="e.g. 1.0"
              />
            </div>
            <div>
              <Label>Cavities</Label>
              <Input
                type="number" min="1"
                value={subMouldForm.cavities}
                onChange={(e) => setSubMouldForm({ ...subMouldForm, cavities: e.target.value })}
              />
            </div>
            <div className="col-span-2 rounded-md bg-indigo-50 border border-indigo-200 p-2 text-xs text-indigo-900">
              <b>Inherited from parent:</b>{" "}
              {(showSubMould?.polymer_components || []).length > 0
                ? showSubMould?.polymer_components.map(c => `${c.polymer_rm_id}@${c.percentage}%`).join(" + ")
                : (showSubMould?.polymer_rm_id || "—")}
              {" · "}Location: {showSubMould?.location_type === "VENDOR" ? `Vendor · ${showSubMould?.vendor_name || "—"}` : (showSubMould?.branch_name || "—")}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSubMould(null)}>Cancel</Button>
            <Button
              onClick={saveSubMould}
              disabled={!subMouldForm.mould_code || !subMouldForm.per_unit_weight_g}
              data-testid="submould-save"
            >Create Sub-Mould</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Physical Copies Dialog */}
      <Dialog open={!!showCopies} onOpenChange={() => setShowCopies(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="copies-dialog">
          <DialogHeader>
            <DialogTitle>Physical Copies · <span className="font-mono">{showCopies?.mould_code}</span></DialogTitle>
            <DialogDescription>
              Track additional physical instances of this mould. All copies share the same <b>mould code</b> and BOM;
              only location varies per copy.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            {(showCopies?.physical_copies || []).length === 0 ? (
              <div className="text-sm text-muted-foreground p-4 border rounded-md text-center bg-slate-50">
                No physical copies tracked yet. The primary mould card is copy&nbsp;#1 by default.
              </div>
            ) : (
              <div className="border rounded-md overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-xs">
                    <tr>
                      <th className="text-left p-2">#</th>
                      <th className="text-left p-2">Location</th>
                      <th className="text-left p-2">Note</th>
                      <th className="text-left p-2">History</th>
                      <th className="p-2 w-14"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(showCopies?.physical_copies || []).map((c, i) => (
                      <tr key={c.copy_id} className="border-t align-top" data-testid={`copy-row-${i}`}>
                        <td className="p-2 font-mono text-xs">#{i + 2}</td>
                        <td className="p-2 text-xs">
                          {c.location_type === "VENDOR" ? (
                            <span className="text-amber-700 flex items-center gap-1"><Truck className="w-3 h-3" /> {c.vendor_name || c.vendor_id || "—"}</span>
                          ) : (
                            <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {c.branch_name || c.branch_id || "—"}</span>
                          )}
                        </td>
                        <td className="p-2 text-xs text-slate-600">{c.note || "—"}</td>
                        <td className="p-2 text-xs">
                          <Badge variant="outline" className="text-[10px]">{(c.location_history || []).length} entries</Badge>
                        </td>
                        <td className="p-2">
                          <Button
                            size="icon" variant="ghost"
                            title="Move copy to a new location"
                            onClick={() => setCopyMoveState({
                              copy_id: c.copy_id,
                              location_type: c.location_type,
                              branch_id: c.branch_id || "",
                              vendor_id: c.vendor_id || "",
                              note: "",
                            })}
                            data-testid={`move-copy-${i}`}
                          >
                            <ArrowRightLeft className="w-3.5 h-3.5" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Move copy inline form */}
            {copyMoveState && (
              <div className="border rounded-md p-3 bg-amber-50 border-amber-200 space-y-2" data-testid="move-copy-form">
                <div className="text-sm font-semibold">Move copy</div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant={copyMoveState.location_type === "BRANCH" ? "default" : "outline"}
                    onClick={() => setCopyMoveState({ ...copyMoveState, location_type: "BRANCH", vendor_id: "" })}
                  ><MapPin className="w-3 h-3 mr-1" /> Branch</Button>
                  <Button
                    size="sm"
                    variant={copyMoveState.location_type === "VENDOR" ? "default" : "outline"}
                    onClick={() => setCopyMoveState({ ...copyMoveState, location_type: "VENDOR", branch_id: "" })}
                  ><Truck className="w-3 h-3 mr-1" /> Vendor</Button>
                </div>
                {copyMoveState.location_type === "BRANCH" ? (
                  <Select value={copyMoveState.branch_id} onValueChange={(v) => setCopyMoveState({ ...copyMoveState, branch_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Pick a branch" /></SelectTrigger>
                    <SelectContent>{branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                  </Select>
                ) : (
                  <Select value={copyMoveState.vendor_id} onValueChange={(v) => setCopyMoveState({ ...copyMoveState, vendor_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Pick a vendor" /></SelectTrigger>
                    <SelectContent className="max-h-56">{vendors.map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent>
                  </Select>
                )}
                <Input
                  placeholder="Note (optional)"
                  value={copyMoveState.note}
                  onChange={(e) => setCopyMoveState({ ...copyMoveState, note: e.target.value })}
                />
                <div className="flex gap-2 justify-end">
                  <Button size="sm" variant="ghost" onClick={() => setCopyMoveState(null)}>Cancel</Button>
                  <Button size="sm" onClick={moveCopy} data-testid="submit-move-copy">Move</Button>
                </div>
              </div>
            )}

            {/* Add new copy */}
            <div className="border rounded-md p-3 space-y-2">
              <div className="text-sm font-semibold flex items-center gap-2"><Plus className="w-4 h-4" /> Add physical copy</div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant={copyForm.location_type === "BRANCH" ? "default" : "outline"}
                  onClick={() => setCopyForm({ ...copyForm, location_type: "BRANCH", vendor_id: "" })}
                ><MapPin className="w-3 h-3 mr-1" /> Branch</Button>
                <Button
                  size="sm"
                  variant={copyForm.location_type === "VENDOR" ? "default" : "outline"}
                  onClick={() => setCopyForm({ ...copyForm, location_type: "VENDOR", branch_id: "" })}
                ><Truck className="w-3 h-3 mr-1" /> Vendor</Button>
              </div>
              {copyForm.location_type === "BRANCH" ? (
                <Select value={copyForm.branch_id} onValueChange={(v) => setCopyForm({ ...copyForm, branch_id: v })}>
                  <SelectTrigger data-testid="copy-branch-select"><SelectValue placeholder="Pick a branch" /></SelectTrigger>
                  <SelectContent>{branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                </Select>
              ) : (
                <Select value={copyForm.vendor_id} onValueChange={(v) => setCopyForm({ ...copyForm, vendor_id: v })}>
                  <SelectTrigger data-testid="copy-vendor-select"><SelectValue placeholder="Pick a vendor" /></SelectTrigger>
                  <SelectContent className="max-h-56">{vendors.map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent>
                </Select>
              )}
              <Input
                placeholder="Note (optional)"
                value={copyForm.note}
                onChange={(e) => setCopyForm({ ...copyForm, note: e.target.value })}
                data-testid="copy-note-input"
              />
              <Button
                size="sm"
                onClick={addCopy}
                disabled={copyForm.location_type === "BRANCH" ? !copyForm.branch_id : !copyForm.vendor_id}
                data-testid="submit-add-copy"
              ><Plus className="w-3.5 h-3.5 mr-1" /> Add copy</Button>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCopies(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Documents Dialog */}
      <Dialog open={!!showDocs} onOpenChange={() => setShowDocs(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="docs-dialog">
          <DialogHeader>
            <DialogTitle>Documents · <span className="font-mono">{showDocs?.mould_code}</span></DialogTitle>
            <DialogDescription>
              Attach drawings, trial reports, maintenance logs. 25 MB per file.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="border rounded-md p-3 bg-slate-50 space-y-2">
              <div className="text-sm font-semibold flex items-center gap-2"><Upload className="w-4 h-4" /> Upload new document</div>
              <div className="flex gap-2 items-center flex-wrap">
                <Select value={docType} onValueChange={setDocType}>
                  <SelectTrigger className="w-56" data-testid="doc-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="DRAWING">Drawing</SelectItem>
                    <SelectItem value="TRIAL_REPORT">Trial Report</SelectItem>
                    <SelectItem value="MAINTENANCE_LOG">Maintenance Log</SelectItem>
                    <SelectItem value="SPEC_SHEET">Spec Sheet</SelectItem>
                    <SelectItem value="OTHER">Other</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  type="file"
                  onChange={(e) => setDocFile(e.target.files?.[0] || null)}
                  className="flex-1"
                  data-testid="doc-file-input"
                />
                <Button size="sm" onClick={uploadDoc} disabled={!docFile || docUploading} data-testid="doc-upload-btn">
                  {docUploading ? "Uploading…" : "Upload"}
                </Button>
              </div>
              {docFile && (
                <div className="text-xs text-muted-foreground">Selected: {docFile.name} · {(docFile.size / 1024).toFixed(0)} KB</div>
              )}
            </div>

            {docsLoading ? (
              <div className="py-6 text-center text-muted-foreground text-sm">Loading documents…</div>
            ) : docs.length === 0 ? (
              <div className="p-6 text-center text-sm text-muted-foreground border rounded-md">
                No documents attached yet.
              </div>
            ) : (
              <div className="border rounded-md overflow-hidden">
                <table className="w-full text-sm" data-testid="docs-table">
                  <thead className="bg-slate-50 text-xs">
                    <tr>
                      <th className="text-left p-2">Type</th>
                      <th className="text-left p-2">Filename</th>
                      <th className="text-left p-2">Size</th>
                      <th className="text-left p-2">Uploaded</th>
                      <th className="p-2 w-24"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {docs.map((d) => (
                      <tr key={d.doc_id} className="border-t" data-testid={`doc-row-${d.doc_id}`}>
                        <td className="p-2"><Badge variant="outline" className="text-[10px]">{d.doc_type}</Badge></td>
                        <td className="p-2 text-xs">{d.filename}</td>
                        <td className="p-2 text-xs text-muted-foreground">{d.size ? `${Math.round(d.size / 1024)} KB` : "—"}</td>
                        <td className="p-2 text-xs text-muted-foreground">{d.uploaded_at ? new Date(d.uploaded_at).toLocaleDateString() : "—"}</td>
                        <td className="p-2 flex gap-1">
                          <Button size="icon" variant="ghost" onClick={() => downloadDoc(d)} title="Download" data-testid={`doc-download-${d.doc_id}`}>
                            <Download className="w-3.5 h-3.5" />
                          </Button>
                          <Button size="icon" variant="ghost" onClick={() => deleteDoc(d)} title="Delete" data-testid={`doc-delete-${d.doc_id}`}>
                            <X className="w-3.5 h-3.5 text-red-500" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDocs(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Feb 2026 — Sub-Mould Bulk Upload dialog */}
      <Dialog open={showSubBulk} onOpenChange={(o) => { setShowSubBulk(o); if (!o) { setSubBulkFile(null); setSubBulkResult(null); } }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="sub-mould-bulk-dialog">
          <DialogHeader>
            <DialogTitle>Sub-Mould Bulk Upload</DialogTitle>
            <DialogDescription>
              Create or update family sub-moulds from Excel. Rows are keyed by (Parent Mould Code + Child Suffix).
              Polymer / Location / Branch / Vendor columns are rejected — those are parent-level.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2 items-center flex-wrap">
              <Button variant="outline" size="sm" onClick={downloadSubBulkTemplate} data-testid="sub-mould-template-btn">
                <Download className="w-4 h-4 mr-2" /> Download Template
              </Button>
              <Input type="file" accept=".xlsx,.xls" onChange={(e) => setSubBulkFile(e.target.files?.[0] || null)}
                     className="flex-1 min-w-64" data-testid="sub-mould-file-input" />
              <label className="text-sm flex items-center gap-2">
                <input type="checkbox" checked={subBulkDryRun} onChange={(e) => setSubBulkDryRun(e.target.checked)}
                       data-testid="sub-mould-dry-run" /> Dry Run
              </label>
              <Button onClick={submitSubBulkUpload} disabled={!subBulkFile || subBulkBusy} data-testid="sub-mould-submit-btn">
                {subBulkBusy ? "Uploading..." : "Upload"}
              </Button>
            </div>
            {subBulkResult && (
              <div className="border rounded-md p-3 bg-slate-50 text-sm space-y-2">
                <div className="font-semibold">
                  {subBulkResult.dry_run ? "Dry-Run Receipt" : "Upload Receipt"}
                </div>
                <div className="flex gap-4 flex-wrap">
                  <span>Created: <strong className="text-green-700">{subBulkResult.summary.created}</strong></span>
                  <span>Updated: <strong className="text-blue-700">{subBulkResult.summary.updated}</strong></span>
                  <span>Errors: <strong className="text-red-700">{subBulkResult.summary.errors}</strong></span>
                </div>
                <div className="max-h-56 overflow-y-auto border rounded bg-white">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-100">
                      <tr><th className="text-left px-2 py-1">Row</th><th className="text-left px-2 py-1">Action</th><th className="text-left px-2 py-1">Mould / Error</th></tr>
                    </thead>
                    <tbody>
                      {subBulkResult.rows.map((r, i) => (
                        <tr key={i} className={r.action === "error" ? "bg-red-50" : ""} data-testid={`sub-bulk-row-${i}`}>
                          <td className="px-2 py-1">{r.row}</td>
                          <td className="px-2 py-1">{r.action}</td>
                          <td className="px-2 py-1 font-mono">{r.mould_code || r.error}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSubBulk(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Feb 2026 — Family Parent Cleanup result dialog */}
      <Dialog open={!!showCleanupResult} onOpenChange={(o) => { if (!o) setShowCleanupResult(null); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="cleanup-result-dialog">
          <DialogHeader>
            <DialogTitle>Family Parent Cleanup — Results</DialogTitle>
            <DialogDescription>
              Cleared stored part fields on family parents whose INPs have all been retagged to sub-moulds.
              Parents with legacy INPs still directly tagged were skipped.
            </DialogDescription>
          </DialogHeader>
          {showCleanupResult && (
            <div className="space-y-3 text-sm">
              <div className="flex gap-4 flex-wrap">
                <span>Cleared: <strong className="text-green-700">{showCleanupResult.cleaned?.length || 0}</strong></span>
                <span>Skipped: <strong className="text-amber-700">{showCleanupResult.skipped?.length || 0}</strong></span>
                <span>Unchanged: <strong className="text-slate-600">{showCleanupResult.no_change?.length || 0}</strong></span>
              </div>
              {showCleanupResult.cleaned?.length > 0 && (
                <div>
                  <div className="font-semibold text-green-700 mb-1">Cleared</div>
                  <div className="font-mono text-xs bg-green-50 border rounded p-2">
                    {showCleanupResult.cleaned.map(c => c.mould_code).join(", ")}
                  </div>
                </div>
              )}
              {showCleanupResult.skipped?.length > 0 && (
                <div>
                  <div className="font-semibold text-amber-700 mb-1">Skipped (still have direct-linked INPs)</div>
                  <div className="border rounded max-h-56 overflow-y-auto">
                    {showCleanupResult.skipped.map((s, i) => (
                      <div key={i} className="p-2 border-b last:border-0 text-xs" data-testid={`cleanup-skipped-${i}`}>
                        <div className="font-mono font-semibold">{s.mould_code}</div>
                        <div className="text-muted-foreground">Blocking INPs: {s.blocking_inp_rm_ids?.join(", ")}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCleanupResult(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Feb 2026 — Adopt Orphan Sub-Moulds dialog (dry-run plan + apply). */}
      <Dialog open={!!adoptPlan} onOpenChange={(o) => { if (!o) { setAdoptPlan(null); setAdoptApplied(null); } }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="adopt-orphan-result-dialog">
          <DialogHeader>
            <DialogTitle>Adopt Orphan Sub-Moulds — {adoptApplied ? "Applied" : "Plan"}</DialogTitle>
            <DialogDescription>
              Flat &quot;PARENT/N&quot; sub-moulds (created historically by the generic Bulk Upload without parent linkage) will be
              nested under their proper family header. Idempotent — re-running after apply is safe.
            </DialogDescription>
          </DialogHeader>
          {adoptPlan && (
            <div className="space-y-3 text-sm">
              <div className="flex gap-4 flex-wrap">
                <span>Headers to create: <strong className="text-green-700" data-testid="adopt-headers-created">{(adoptApplied || adoptPlan).summary.headers_created}</strong></span>
                <span>Reactivated: <strong className="text-blue-700" data-testid="adopt-headers-reactivated">{(adoptApplied || adoptPlan).summary.headers_reactivated}</strong></span>
                <span>Reused: <strong className="text-slate-600" data-testid="adopt-headers-reused">{(adoptApplied || adoptPlan).summary.headers_reused}</strong></span>
                <span>Children to link: <strong className="text-green-700" data-testid="adopt-children-linked">{(adoptApplied || adoptPlan).summary.children_linked}</strong></span>
                <span>Skipped: <strong className="text-amber-700" data-testid="adopt-skipped">{(adoptApplied || adoptPlan).summary.skipped}</strong></span>
              </div>
              {(adoptApplied || adoptPlan).headers?.length > 0 && (
                <div>
                  <div className="font-semibold mb-1">Family headers</div>
                  <div className="border rounded max-h-56 overflow-y-auto text-xs font-mono">
                    {(adoptApplied || adoptPlan).headers.map((h, i) => (
                      <div key={i} className="p-2 border-b last:border-0 flex justify-between" data-testid={`adopt-header-row-${i}`}>
                        <span className="font-semibold">{h.mould_code}</span>
                        <span className={
                          h.action === "created" ? "text-green-700"
                          : h.action === "reactivated" ? "text-blue-700"
                          : "text-slate-500"
                        }>{h.action}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {(adoptApplied || adoptPlan).links?.length > 0 && (
                <div>
                  <div className="font-semibold mb-1">Children to link ({(adoptApplied || adoptPlan).links.length})</div>
                  <div className="border rounded max-h-56 overflow-y-auto text-xs font-mono">
                    {(adoptApplied || adoptPlan).links.map((l, i) => (
                      <div key={i} className="p-2 border-b last:border-0" data-testid={`adopt-link-row-${i}`}>
                        <span>{l.child_code}</span>
                        <span className="text-muted-foreground"> → </span>
                        <span className="font-semibold">{l.parent_code}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {(adoptApplied || adoptPlan).skipped?.length > 0 && (
                <div>
                  <div className="font-semibold text-amber-700 mb-1">Skipped</div>
                  <div className="border rounded max-h-56 overflow-y-auto text-xs">
                    {(adoptApplied || adoptPlan).skipped.map((s, i) => (
                      <div key={i} className="p-2 border-b last:border-0" data-testid={`adopt-skipped-row-${i}`}>
                        <div className="font-mono font-semibold">{s.mould_code}</div>
                        <div className="text-muted-foreground">{s.reason}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {adoptApplied && (
                <div className="rounded border border-green-300 bg-green-50 p-2 text-green-800 text-xs" data-testid="adopt-applied-banner">
                  Applied. The library will refresh in the background — new family headers appear immediately.
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            {!adoptApplied && adoptPlan && (
              <Button
                onClick={runAdoptOrphansApply}
                disabled={adoptBusy || ((adoptPlan.summary.children_linked + adoptPlan.summary.headers_created + adoptPlan.summary.headers_reactivated) === 0)}
                data-testid="adopt-apply-btn"
              >
                {adoptBusy ? "Applying..." : "Apply"}
              </Button>
            )}
            <Button variant="outline" onClick={() => { setAdoptPlan(null); setAdoptApplied(null); }} data-testid="adopt-close-btn">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
